import {useState, useEffect, useMemo, useCallback} from 'react';
import { useMediaQuery } from '@mui/material';
import ErrorHandler from '@Root/ErrorHandler';
import DataTable from 'react-data-table-component';
import FormUserAccessLinkService from '@Helpers/URLHelpers/KaiziqResolve/views/FormUserAccessLinkService';
import TicketUserEmailCountViewService from '@Helpers/URLHelpers/KaiziqResolve/views/TicketUserEmailCountViewService';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './FormUserList.scss';

export default function FormUserList(props) {

    const [kaiziqResolveConnection, setKaiziqResolveConnection] = useState(props.kaiziqResolveConnection);
    const [, setFormUserList] = useState([]);
    const [dataList, setDataList] = useState([]);
    const [, setTicketUserEmailCount] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const is0To768 = useMediaQuery('(max-width: 768px)');
    const errorHandler = new ErrorHandler();
    const formUserAccessLinkService = new FormUserAccessLinkService();
    const ticketUserEmailCountViewService = new TicketUserEmailCountViewService();
    const className = 'formUserList';

    useEffect(() => {
        setKaiziqResolveConnection(props.kaiziqResolveConnection);
    }, [props.kaiziqResolveConnection]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kaiziqResolveConnection?.id, kaiziqResolveConnection?.visibility]);

    const loadData = async () => {
        if(!kaiziqResolveConnection?.id) return;
        setLoading(true);
        setError(null);
        let data = [];
        try {
            if(kaiziqResolveConnection?.visibility === 'public') {
                let ticketUserEmailCountObj = await ticketUserEmailCountViewService.getListByForm(kaiziqResolveConnection?.id);
                ticketUserEmailCountObj = JSON.parse(ticketUserEmailCountObj.body);
                setTicketUserEmailCount(ticketUserEmailCountObj);
                for(let i = 0; i < ticketUserEmailCountObj.length; i++) {
                    data.push({
                        reporterName: ticketUserEmailCountObj[i].createdByEmail,
                        createTimeStamp: ticketUserEmailCountObj[i].latestTicketCreateTimeStamp,
                        tickets: ticketUserEmailCountObj[i].ticketCount
                    });
                }
            } else {
                let formUserListObj = await formUserAccessLinkService.getListByForm(kaiziqResolveConnection?.id);
                formUserListObj = JSON.parse(formUserListObj.body);
                setFormUserList(formUserListObj);
                for(let i = 0; i < formUserListObj.length; i++) {
                    data.push({
                        reporterName: formUserListObj[i].createdByEmail,
                        createTimeStamp: formUserListObj[i].latestTicketCreateTimeStamp,
                        tickets: formUserListObj[i].ticketCount
                    });
                }
            }
            setDataList(data);
        } catch (err) {
            setError('Failed to load reporters');
            errorHandler.handleError(err);
        }
        setLoading(false);
        setRefreshing(false);
    };

    const handleRefresh = () => {
        if(refreshing) return;
        setRefreshing(true);
        loadData();
    };

    const formatDate = useCallback((ts) => {
        try {
            return new Intl.DateTimeFormat('en-GB', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit'
            }).format(ts);
        } catch {
            return '-';
        }
    }, []);

    const filteredData = useMemo(() => {
        if(!search.trim()) return dataList;
        const s = search.toLowerCase();
        return dataList.filter(r => (r.reporterName || '').toLowerCase().includes(s));
    }, [dataList, search]);

    const totalTickets = useMemo(() => dataList.reduce((a,b) => a + (b.tickets || 0), 0), [dataList]);
    const totalReporters = dataList.length;

    const CustomName = ({ row }) => (
        <div className={className + '__cellName'}>
            <span title={row.reporterName}>{row.reporterName}</span>
        </div>
    );

    const CustomDate = ({ row }) => (
        <div className={className + '__cellDate'}>
            {formatDate(row?.createTimeStamp)}
        </div>
    );

    const CustomTickets = ({ row }) => (
        <div className={className + '__cellTickets'}>
            {row.tickets}
        </div>
    );

    const columns = [
        {
            name: 'Reporter',
            selector: row => row.reporterName,
            sortable: true,
            grow: 2,
            cell: row => <CustomName row={row} />
        },
        {
            name: 'Last Reported',
            selector: row => row.createTimeStamp,
            sortable: true,
            grow: 2,
            cell: row => <CustomDate row={row} />
        },
        {
            name: 'Tickets',
            selector: row => row.tickets,
            sortable: true,
            right: true,
            grow: 1,
            cell: row => <CustomTickets row={row} />
        }
    ];

    return (
        <div className={className} id='formUserList'>

            <div className={className + '__header'}>
                <div className={className + '__header__left'}>
                    <div className={className + '__title'}>Reporters</div>
                </div>
                <div className={className + '__header__actions'}>
                    <TextField
                        size="small"
                        placeholder="Search reporter..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 4 }} />
                        }}
                        className={className + '__search'}
                    />
                    <div className={className + '__chips'}>
                        <Chip
                            size="small"
                            icon={<PeopleOutlineIcon />}
                            label={totalReporters + ' reporters'}
                            className={className + '__chip'}
                        />
                        <Chip
                            size="small"
                            icon={<ConfirmationNumberOutlinedIcon />}
                            label={totalTickets + ' tickets'}
                            className={className + '__chip'}
                        />
                        <Chip
                            size="small"
                            label={kaiziqResolveConnection?.visibility === 'public' ? 'Public' : 'Private'}
                            className={className + '__chip'}
                        />
                    </div>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={handleRefresh} className={refreshing ? 'rotating' : ''}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {loading && (
                <div className={className + '__loader'}>
                    <Skeleton variant="rectangular" height={180} style={{ borderRadius: 14 }} />
                </div>
            )}

            {!loading && error && (
                <div className={className + '__error'}>
                    {error}
                </div>
            )}

            {!loading && !error && filteredData.length === 0 && (
                <div className={className + '__empty'}>
                    No reporters found
                </div>
            )}

            {!loading && !error && filteredData.length > 0 && (
                <div className={className + '__tableWrap'}>
                    <DataTable
                        className={className + '__table'}
                        columns={columns}
                        data={filteredData}
                        pagination
                        dense={is0To768}
                        highlightOnHover
                        persistTableHead
                        responsive
                        striped
                        customStyles={{
                            headCells: {
                                style: {
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: 'var(--color-text)',
                                    backgroundColor: 'var(--color-componentBackground)',
                                    borderBottom: '1px solid var(--color-darkBorder)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '.5px'
                                }
                            },
                            headRow: {
                                style: {
                                    backgroundColor: 'var(--color-componentBackground)'
                                }
                            },
                            rows: {
                                style: {
                                    minHeight: '44px',
                                    backgroundColor: 'var(--color-componentBackground)',
                                    color: 'var(--color-text)',
                                    borderBottom: '1px solid var(--color-darkBorder)'
                                }
                            },
                            cells: {
                                style: {
                                    fontSize: '0.875rem',
                                    color: 'var(--color-text)'
                                }
                            },
                            pagination: {
                                style: {
                                    backgroundColor: 'var(--color-componentBackground)',
                                    color: 'var(--color-primaryGrey)',
                                    borderTop: '1px solid var(--color-darkBorder)'
                                }
                            }
                        }}
                    />
                </div>
            )}
        </div>
    );
}
